import { Form, Formik } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { DropdownOptions, FieldControllerTypes, FieldTypes } from '../../form_v2/types';
import HouseIcon from '@mui/icons-material/House';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import DialogAction from '../../Dialog/DialogAction';
import { Button } from '@mui/material';
import FormLayout from '../../layout/Form/FormLayout';
import { VerticalStepper } from '../../Stepper';
import FormActionButtonLayout from '../../layout/Form/FormActionButtonLayout';
import { isSubmitEnabled } from '../../../lib/helperFuncs';
import { formInStepperBuilder } from '../../form_v2/helpers';
import { LocationFormProps } from './Types';
import FeedIcon from '@mui/icons-material/Feed';
import { LocationRequestValidation } from './validationSchema';

const LocationFormRequestSteps = ({ initialValues,
  onSubmit }: LocationFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [openSubmit, setOpenSubmit] = useState<boolean>(false);
  const [openCancel, setOpenCancel] = useState<boolean>(false);
  

  const hours: DropdownOptions[] = [
    { key: t('Πρωινές (05:00 πμ - 12:00 μμ)'), value: 1 },
    { key: t('Μεσημεριανές (12:00 μμ - 05:00 μμ)'), value: 2 },
    { key: t('Απογευματινές (05:00 μμ - 09:00 μμ)'), value: 3 },
    { key: t('Βραδινές (09:00 μμ - 04:00 πμ)'), value: 4 },
  ];

  const step1Form = [
    [{
      control: FieldControllerTypes.input,
      label: t('Επωνυμία Εταιρείας'),
      name: 'title',
      required: true
    },
    {
      control: FieldControllerTypes.input,
      label: t('ΑΦΜ'),
      name: 'afm',
      required: true
    }],
    [{
      control: FieldControllerTypes.telInput,
      label: t('Τηλέφωνο'),
      name: 'phone_number',
      required: true,
    },
    {
      control: FieldControllerTypes.input,
      type: 'search',
      label: t('Email'),
      name: 'email',
      required: true,
    }]];

  const step2Form = [
    [{
      control: FieldControllerTypes.date,
      name: 'dateFrom',
      label: t('Ημερομηνία Από *'),
      customLabel: true,
      required: true
    },
    {
      control: FieldControllerTypes.date,
      name: 'dateTo',
      label: t('Ημερομηνία Έως *'),
      customLabel: true,
      required: true,
      info: true,
      tooltip: t('Πρέπει να συμπληρώσετε το πεδίο με ημερομηνία ίδια ή αργότερα απο την ημερομηνία στο πεδίο Ημερομηνία Από')
    }],
    [{
      control: FieldControllerTypes.autocomplete,
      label: t('Ενδεικτικές Ώρες'),
      name: 'hours',
      options: hours,
      customLabel: true,
      required: true
    }],
    [{
      control: FieldControllerTypes.textarea,
      label: t('Διευκρινήσεις / Ερωτήσεις'),
      placeholder: t('Προσθέστε επιπλέον διευκρινήσεις σχετικά με τις ανάγκες του γυρίσματος. Εισάγετε ερωτήματα ή ζητήστε συγκεκριμένες πληροφορίες για την τοποθεσία και τις ανάγκες αδειοδότησης.'),
      name: 'info',
      required: true
    }]
  ];

  const step3Form = [
    ...(initialValues.name 
      ? [{
        control: FieldControllerTypes.input,
        type: FieldTypes.text,
        label: t('Όνομα'),
        name: 'name',
        required: true,
        disabled: true
      }]
      : []), 
    ...(initialValues.surname 
      ? [{
        control: FieldControllerTypes.input,
        type: FieldTypes.text,
        label: t('Επώνυμο'),
        name: 'surname',
        required: true,
        disabled: true
      }]
      : []), 
    [{
      control: FieldControllerTypes.telInput,
      type: FieldTypes.text,
      label: t('Τηλέφωνο'),
      name: 'tel',
      required: true,
      disabled: false
    },
    {
      control: FieldControllerTypes.input,
      label: t('Email'),
      name: 'email_manage',
      required: true,
      disabled: true
    }]
  ];

  const step4Form = [
    {
      control: FieldControllerTypes.checkboxTerms,
      type: FieldTypes.checkboxTerms,
      label: t('Αποδοχή όρων Χρήσης & Ορθότητας Στοιχείων'),
      name: 'termsAndConditions',
      required: true,
      link: true
    } 
  ];

  const step1 = {
    label: t('Βασικά Στοιχεία Εταιρείας Παραγωγής'),
    content: step1Form,
    icon: <HouseIcon />
  };
  const step2 = {
    label: t('Στοιχεία Γυρίσματος'),
    content: step2Form,
    icon: <LocationOnIcon />
  };
  const step3 = {
    label: t('Στοιχεία υπεύθυνου επικοινωνίας'),
    content: step3Form,
    icon: <PeopleIcon />
  };
  const step4 = {
    label: t('Αποδοχή όρων Χρήσης & Ορθότητας Στοιχείων'),
    content: step4Form,
    icon: <FeedIcon />
  };

  const wholeForm = [
    { ...step1 },
    { ...step2 },
    { ...step3 },
    { ...step4 }
  ];

  return (
    <Formik
      enableReinitialize
      onSubmit={() => undefined}
      initialValues={initialValues}
      validationSchema={() => LocationRequestValidation(
        initialValues?.name || '', 
        initialValues.surname || '')}
      validateOnBlur={false}
    >
      {formik =>
        (<div>
          <Form>
            <FormLayout>
              <VerticalStepper activeStep={1}>
                {formInStepperBuilder(wholeForm, formik)}
              </VerticalStepper>
            </FormLayout>
            <FormActionButtonLayout>
              <Button variant='outlined' color='error'
                onClick={() => setOpenCancel(true)}>
                {t('Ακύρωση')}
              </Button>              
              <Button variant='contained'
                disabled={isSubmitEnabled(formik?.errors,formik?.dirty)}
                onClick={() => setOpenSubmit(true)}>
                {t('Υποβολή')}
              </Button>
              <DialogAction open={openCancel} handleClose={() => setOpenCancel(false)}
                title={String(t('Ακύρωση δημιουργίας Αιτήματος'))}
                content={t('Ειστε σιγουρος/η ότι θέλετε να ακυρώσετε το αίτημα;')}
                onAccept={() => {
                  setOpenCancel(false);
                  navigate(-1);
                }} />
              <DialogAction open={openSubmit} handleClose={() => setOpenSubmit(false)}
                title={String(t('Επιβεβαίωση Υποβολής'))}
                content={t('Είστε σίγουρος/η ότι θέλετε να προχωρήσετε με την υποβολή της αίτησης;')}
                onAccept={() => {
                  setOpenSubmit(false);
                  onSubmit(formik.values);
                }} />
            </FormActionButtonLayout>
          </Form>
        </div>)
      }
    </Formik>
  );
};

export default LocationFormRequestSteps;